import { Navigate, useLocation } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";
import { getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useEffect, useState } from "react";
import { withDeadline } from "../lib/withDeadline";

// Same budget and same meaning as LoginPage's own session check: a
// getCurrentUser() that has not answered in time counts as "not signed in".
// With expired tokens it refreshes over the network, with no timeout, and
// on the iPad a stalled refresh left a loading overlay up forever.
const SESSION_CHECK_TIMEOUT_MS = 10_000;

interface PrivateRouteProps {
  children: React.ReactNode;
  /** Where an unauthenticated visitor is sent. Every app (frontend, admin,
   * auditor) hosts its own local /login (shared LoginPage from this
   * package), so this defaults to a same-origin path. */
  loginUrl?: string;
}

export function PrivateRoute({
  children,
  loginUrl = "/login",
}: PrivateRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    withDeadline(getCurrentUser(), SESSION_CHECK_TIMEOUT_MS, "session check")
      .then(() => {
        if (mounted) setIsAuthenticated(true);
      })
      .catch(() => {
        // Only settles a check still pending: a 'signedIn' Hub event that
        // landed while getCurrentUser() was stalled already answered it, and
        // the deadline firing later must not sign that user back out.
        if (mounted) setIsAuthenticated((prev) => prev ?? false);
      });

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (!mounted) return;
      if (payload.event === "signedIn") setIsAuthenticated(true);
      if (
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh_failure"
      ) {
        setIsAuthenticated(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  if (isAuthenticated === null) return <LoadingOverlay visible />;
  if (!isAuthenticated) {
    return <Navigate to={loginUrl} state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
